import axios from 'axios';

import { API_KEY, URL } from '~/common/helpers/environment';
import { Folder } from '~/entities/Folder';

type FetchAllParams = {
  realm: string;
  token: string;
};

type FetchByProjectParams = {
  realm: string;
  token: string;
  projectId: string;
};

type SaveParams = {
  realm: string;
  token: string;
  projectId: string;
  name: string;
};

type EditParams = {
  realm: string;
  token: string;
  projectId: string;
  folderId: string;
  name: string;
};

type RemoveParams = {
  realm: string;
  token: string;
  projectId: string;
  folderId: string;
};

type MovePipelineToFolderParams = {
  realm: string;
  token: string;
  projectId: string;
  pipelineName: string;
  pipelineVersionMajor: number;
  folderIdOrName?: string;
};

type ExistsByNameParams = {
  realm: string;
  token: string;
  projectId: string;
  name: string;
};

const folder = {
  async fetchAll(params: FetchAllParams): Promise<{ folders: Folder[] }> {
    const { data } = await axios.get<Folder[]>(
      `${URL}/design/realms/${encodeURIComponent(params.realm)}/folders`,
      {
        headers: {
          authorization: params.token,
          apiKey: API_KEY,
          'Content-Type': 'application/json'
        }
      }
    );
    return { folders: data };
  },
  async fetchByProject(
    params: FetchByProjectParams
  ): Promise<{ folders: Folder[] }> {
    const { data } = await axios.get<Folder[]>(
      `${URL}/design/realms/${encodeURIComponent(params.realm)}/project/${encodeURIComponent(params.projectId)}/folders`,
      {
        headers: {
          authorization: params.token,
          apiKey: API_KEY,
          'Content-Type': 'application/json'
        }
      }
    );
    return { folders: data };
  },
  async save(params: SaveParams): Promise<{ folder: Folder }> {
    const { data } = await axios.post<Folder>(
      `${URL}/design/realms/${encodeURIComponent(params.realm)}/projects/${encodeURIComponent(params.projectId)}/folders`,
      {
        name: params.name
      },
      {
        headers: {
          authorization: params.token,
          apiKey: API_KEY,
          'Content-Type': 'application/json'
        }
      }
    );
    return { folder: data };
  },
  async edit(params: EditParams): Promise<{ folder: Folder }> {
    const { data } = await axios.put<Folder>(
      `${URL}/design/realms/${encodeURIComponent(params.realm)}/projects/${encodeURIComponent(params.projectId)}/folders/${encodeURIComponent(params.folderId)}`,
      {
        name: params.name
      },
      {
        headers: {
          authorization: params.token,
          apiKey: API_KEY,
          'Content-Type': 'application/json'
        }
      }
    );
    return { folder: data };
  },
  async remove(params: RemoveParams): Promise<{ success: boolean }> {
    await axios.delete(
      `${URL}/design/realms/${encodeURIComponent(params.realm)}/projects/${encodeURIComponent(params.projectId)}/folders/${encodeURIComponent(params.folderId)}`,
      {
        headers: {
          authorization: params.token,
          apiKey: API_KEY,
          'Content-Type': 'application/json'
        }
      }
    );
    return { success: true };
  },
  async movePipelineToFolder(
    params: MovePipelineToFolderParams
  ): Promise<{ success: boolean }> {
    await axios.post(
      `${URL}/design/realms/${encodeURIComponent(params.realm)}/projects/${encodeURIComponent(params.projectId)}/pipelines/${encodeURIComponent(params.pipelineName)}/versionMajor/${encodeURIComponent(String(params.pipelineVersionMajor))}/folders`,
      {},
      {
        params: {
          folderIdOrName: params.folderIdOrName || ''
        },
        headers: {
          authorization: params.token,
          apiKey: API_KEY,
          'Content-Type': 'application/json'
        }
      }
    );
    return { success: true };
  },
  async existsByName(params: ExistsByNameParams): Promise<{ exists: boolean }> {
    const { data } = await axios.get<boolean>(
      `${URL}/design/realms/${encodeURIComponent(params.realm)}/projects/${encodeURIComponent(params.projectId)}/folders/exists`,
      {
        params: {
          name: params.name
        },
        headers: {
          authorization: params.token,
          apiKey: API_KEY,
          'Content-Type': 'application/json'
        }
      }
    );
    return { exists: data };
  }
};

export default folder;
